import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { LevelBadge } from "@/components/LevelBadge";
import { TsbGauge } from "@/components/charts/TsbGauge";
import { SkillRadar } from "@/components/charts/SkillRadar";
import { GrowthChart } from "@/components/charts/GrowthChart";
import { ScoreBars } from "@/components/charts/ScoreBars";
import { InterestProfileCard } from "@/components/dashboard/InterestProfileCard";
import { DOMAINS, SKILLS, domainColor, skillMeta } from "@/lib/framework";
import { Sparkles, TrendingUp, Target, ShieldCheck, GraduationCap, BadgeCheck } from "lucide-react";

function SectionTitle({ icon: Icon, children, right }) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-[#003057]">
        <Icon className="h-5 w-5 text-[#78be21]" /> {children}
      </h3>
      {right}
    </div>
  );
}

export function CareerCard({ c, showDelta, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-slate-200 bg-white p-4 card-elevate">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-heading text-base font-bold text-[#003057]">{c.title}</p>
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">{c.industry}</p>
        </div>
        <div className="text-right">
          <span className="font-mono-num text-2xl font-bold text-[#005291]">{c.alignment}%</span>
          {showDelta && c.delta !== undefined && c.delta !== 0 && (
            <p className={`text-xs font-bold ${c.delta > 0 ? "text-emerald-600" : "text-amber-600"}`}>
              {c.delta > 0 ? "▲" : "▼"} {Math.abs(c.delta)}
            </p>
          )}
        </div>
      </div>
      <Progress value={c.alignment} className="mt-3 h-2" />
      <div className="mt-3 flex flex-wrap gap-1.5">
        {c.strengths?.slice(0, 2).map((s) => (
          <span key={s.id} className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">
            ✓ {skillMeta(s.id)?.short}
          </span>
        ))}
        {c.gaps?.slice(0, 2).map((g) => (
          <span key={g.id} className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">
            ↑ {skillMeta(g.id)?.short}
          </span>
        ))}
      </div>
    </div>
  );
}

export function ProfileView({ data, careerLimit = 6, showInterestProfile = false }) {
  const p = data.profile;
  const radarSeries = [{ key: "score", name: "Coursework", color: "#005291", scores: p.skill_scores }];
  if (data.verified_profile) {
    radarSeries.push({ key: "verified", name: "Employer-verified", color: "#78be21",
      scores: data.verified_profile.skill_scores, fillOpacity: 0.18 });
  }
  const topGaps = [...p.skills].sort((a, b) => a.score - b.score).slice(0, 3);

  return (
    <div className="space-y-6">
      {/* headline row */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="flex flex-col items-center justify-center p-6 card-elevate">
          <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-slate-400">Transferable Skills Benchmark</p>
          <TsbGauge tsb={p.tsb} tsb100={p.tsb_100} testId="tsb-gauge" />
          <div className="flex items-center gap-2">
            <span className="font-mono-num text-sm font-bold text-slate-500">{p.tsb_100}/100</span>
            <LevelBadge level={p.level} testId="tsb-level-badge" />
          </div>
          {data.work_style && (
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#003057]/6 px-3 py-1 text-xs font-semibold text-[#003057]">
              <Sparkles className="h-3.5 w-3.5 text-[#78be21]" /> Work style: {data.work_style}
            </span>
          )}
        </Card>

        <Card className="p-6 lg:col-span-2 card-elevate">
          <SectionTitle icon={Target}>Domain Breakdown</SectionTitle>
          <ScoreBars
            testId="domain-bars"
            items={p.domains.map((d) => ({ name: d.name, value: d.score, color: d.color }))}
            height={220}
          />
          <div className="mt-2 grid grid-cols-3 gap-2">
            {p.domains.map((d) => (
              <div key={d.id} className="rounded-lg bg-slate-50 p-2.5 text-center">
                <p className="font-mono-num text-lg font-bold" style={{ color: d.color }}>{d.score.toFixed(2)}</p>
                <LevelBadge level={d.level} />
                <p className="mt-1 text-[10px] font-medium text-slate-400">weight {Math.round(d.weight * 100)}%</p>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* radar + growth */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-6 card-elevate">
          <SectionTitle icon={BadgeCheck}>Skill Radar</SectionTitle>
          <SkillRadar series={radarSeries} testId="skill-radar" />
        </Card>
        <Card className="p-6 card-elevate">
          <SectionTitle icon={TrendingUp}>Longitudinal Growth</SectionTitle>
          <GrowthChart growth={data.growth} testId="growth-chart" />
        </Card>
      </div>

      {/* verified signal */}
      {data.verified_signal && (
        <Card className="border-[#78be21]/40 bg-[#78be21]/5 p-6" data-testid="verified-signal-card">
          <SectionTitle icon={ShieldCheck}
            right={<span className="rounded-full bg-[#78be21] px-3 py-1 text-xs font-bold text-[#00243f]">Employer-verified · {Math.round(data.verified_signal.verified_weight * 100)}% weight</span>}>
            Employer 360 Verified Signal
          </SectionTitle>
          <p className="mb-3 text-sm text-slate-600">
            A separate, individually-weighted signal from {data.verified_signal.review_count} employer review(s).
            It never overwrites coursework scores and is fully reversible.
          </p>
          <div className="flex flex-wrap gap-2">
            {data.verified_signal.changed.map((c) => (
              <span key={c.id} className="rounded-lg border border-[#78be21]/40 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">
                {skillMeta(c.id)?.short}: {c.base.toFixed(2)} → <span className="text-[#5fa114]">{c.adjusted.toFixed(2)}</span>
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* skills by domain */}
      <Card className="p-6 card-elevate">
        <SectionTitle icon={BadgeCheck}>10 Skills · Level Badges</SectionTitle>
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {DOMAINS.map((d) => (
            <div key={d.id}>
              <p className="mb-2 flex items-center gap-2 text-sm font-bold" style={{ color: d.color }}>
                <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }} /> {d.short}
              </p>
              <div className="space-y-2">
                {p.skills.filter((s) => s.domain === d.id).map((s) => (
                  <div key={s.id} className="flex items-center justify-between rounded-lg bg-slate-50 px-3 py-2">
                    <span className="text-sm font-medium text-slate-700">{s.name}</span>
                    <span className="flex items-center gap-2">
                      <span className="font-mono-num text-sm font-bold text-slate-800">{s.score.toFixed(2)}</span>
                      <LevelBadge level={s.level} />
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* career matches */}
      <Card className="p-6 card-elevate">
        <SectionTitle icon={Target}>Career Fit</SectionTitle>
        {showInterestProfile && <InterestProfileCard />}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.career_matches.slice(0, careerLimit).map((c, i) => (
            <CareerCard key={c.career_id} c={c} testId={`career-card-${i}`} />
          ))}
        </div>
      </Card>

      {/* recommendations */}
      <Card className="p-6 card-elevate">
        <SectionTitle icon={GraduationCap}>Development Recommendations</SectionTitle>
        <p className="mb-3 text-sm text-slate-500">Lowest skills mapped to Learning Academy micro-modules (placeholders).</p>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          {topGaps.map((s) => (
            <div key={s.id} className="rounded-xl border border-slate-200 p-4">
              <p className="text-sm font-bold text-[#003057]">{s.name}</p>
              <div className="mt-1 flex items-center gap-2">
                <span className="font-mono-num text-sm font-bold text-slate-700">{s.score.toFixed(2)}</span>
                <LevelBadge level={s.level} />
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Recommended: <span className="font-semibold text-[#5fa114]">“{skillMeta(s.id)?.short} Accelerator”</span> module + a project-based checkpoint.
              </p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
